"use client";

import React from "react";
import { Heart, CheckCircle, Download } from "lucide-react";
import { formatInstallCountRow } from "@/lib/format-count";
import { fmtDuration } from "@/lib/tls-format";

export interface CardActionsProps {
  title: string;
  downloads: number;
  /** Seconds. Omitted / non-positive suppresses the duration token. */
  duration?: number;
  /** Over the Tesla limit: the duration token gains a " trim" suffix. */
  needsTrimming?: boolean;
  /**
   * Recency signal (created within the last week). Accepted for call-site
   * compatibility but NO LONGER rendered: the DS SoundRow meta run is
   * `duration · installs` only, and the section context / sort order carry
   * recency. "NEW" in the run always means zero installs (§02.9).
   */
  isNew?: boolean;
  /** Editorial flag. */
  isPremium?: boolean;
  /** This row's preview is playing: the meta run reads "PLAYING". */
  isPlaying?: boolean;
  /** TES-829: expand the icon into a filled CTA once engaged-previewed. */
  hasEngagedPreview: boolean;
  /** W3-1: expand further into a labeled "Download" pill at ≥80% preview. */
  previewCompleted: boolean;
  isDownloading: boolean;
  downloadDone: boolean;
  isSaved: boolean;
  /**
   * Accepted for call-site compatibility; the save control is now ALWAYS
   * kept in the resting mobile row. The ≤639px row briefly showed download
   * only (the heart returned in the expanded plate), while the saved-sounds
   * copy promised "tap the heart on any sound" and the rows had no heart to
   * tap: the control measured 0×0 on every mobile list row. A 44px heart
   * stays on every row, at every width.
   */
  keepSaveOnMobile?: boolean;
  onDownload: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onFavoriteToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * The tokens of the row's single right-aligned meta run, in DS order:
 * duration · installs (with the editorial flag, when true, leading). Zero
 * installs reads "NEW" (§02.9) and that is the ONLY meaning of the word in
 * the run: the recency signal was dropped from the run on every breakpoint
 * so "NEW" never means two things in two positions (F-1).
 */
export function rowMetaTokens(input: {
  downloads: number;
  duration?: number;
  needsTrimming?: boolean;
  /** Ignored (kept so callers need not change); see CardActionsProps.isNew. */
  isNew?: boolean;
  isPremium?: boolean;
}): string[] {
  const { downloads, duration, needsTrimming } = input;
  const tokens: string[] = [];
  // No curation flag in the row run. The DS SoundRow meta is
  // `duration · installs`; "EDITOR'S PICK" was pushed here as a third token
  // and, because every list that sorts by installs floats the picks to the
  // top, it painted on all six visible rows at once. A signal that marks
  // everything marks nothing, and it cost the row its scannable right edge.
  // `isPremium` stays in the props so call sites need no edit, and the flag
  // still reads on the sound detail page where it discriminates.
  const count = formatInstallCountRow(downloads ?? 0);
  if (typeof duration === "number" && duration > 0) {
    tokens.push(needsTrimming ? `${fmtDuration(duration)} trim` : fmtDuration(duration));
  }
  tokens.push(count);
  return tokens;
}

/**
 * Pure presentation: the row's right side — ONE meta run (recency · duration
 * · installs, tabular, right-aligned, immediately before the controls) plus
 * the contextual download button (TES-829 CTA expansion + W3-1 completion
 * pill) and the save heart. All state and handlers come from the parent.
 *
 * Returns a fragment: `.sc-run` and `.sc-card-actions` are both grid items
 * of `.sc-content` (see the grid-template-areas in globals.css), which is
 * what lets the run sit beside the controls on desktop and on the eyebrow
 * line on mobile without a second copy of the text.
 */
export function CardActions({
  title,
  downloads,
  duration,
  needsTrimming = false,
  isPremium = false,
  isPlaying = false,
  hasEngagedPreview,
  previewCompleted,
  isDownloading,
  downloadDone,
  isSaved,
  onDownload,
  onFavoriteToggle,
}: CardActionsProps) {
  const showCta = hasEngagedPreview && !downloadDone;
  // Completion pill: icon + label, wider than the square icon button.
  const showCompletionPill = previewCompleted && !downloadDone;
  const tokens = rowMetaTokens({ downloads, duration, needsTrimming, isPremium });

  return (
    <>
      {/* The one meta cell. DS SoundRow: 12.5px / 600 / tabular, ink-meta;
          "PLAYING" while this row's preview is playing. Each token is its own
          span so the run stays one text run for a reader while the separator
          is decorative. */}
      <span
        className="sc-run"
        data-playing={isPlaying ? "" : undefined}
        title={needsTrimming ? "Over the Tesla limit: trim before installing" : undefined}
      >
        {isPlaying ? (
          <span>PLAYING</span>
        ) : (
          tokens.map((token, i) => (
            // Each token carries its own TRAILING separator so hiding one
            // token (see .sc-run [data-token] in
            // globals.css) removes its separator with it. The install count
            // is always the last token; "NEW" only ever appears there.
            <span
              key={`${token}-${i}`}
            >
              {token}
              {i < tokens.length - 1 && (
                <span aria-hidden="true" className="sc-run-sep">
                  {" · "}
                </span>
              )}
            </span>
          ))
        )}
      </span>

      <div className="sc-card-actions">
        {/* Download button — 44×44 touch target baseline.
            TES-829: expands into a filled CTA once the visitor has
            engaged-previewed (≥2s played). W3-1 Experiment 1: when the
            preview reaches ≥80% completion the button further expands
            into a labeled pill ("Download"), one decisive state change,
            no animation, to maximize signal clarity.
            data-icon-btn opts out of the global touch-target auto-stretch. */}
        <button
          type="button"
          onClick={onDownload}
          data-cuelume-press="press"
          // The card's primary action, so it releases on `pulse` rather than
          // the default tick — Cuelume's recipe for primary controls.
          data-cuelume-release="pulse"
          disabled={isDownloading}
          aria-label={downloadDone ? `LockChime.wav for ${title} downloaded` : `Download LockChime.wav for ${title}`}
          title="Download LockChime.wav"
          data-icon-btn
          className="sc-download tlsr-focus"
          style={{
            height: 44,
            minHeight: 44,
            width: showCompletionPill ? "auto" : 44,
            minWidth: 44,
            padding: showCompletionPill ? "0 14px" : 0,
            gap: showCompletionPill ? 6 : 0,
            // §17.1: a pressed control derives its radius from its shape, never
            // a chosen value. Square icon state → radius-round (a circle);
            // wider-than-tall pill state (post-preview CTA) → radius-pill.
            borderRadius: showCompletionPill ? "var(--ls-radius-pill)" : "var(--ls-radius-round)",
            background: downloadDone
              ? "var(--tls-brand-soft)"
              : (showCta || showCompletionPill)
                ? "var(--tls-fg-strong)"
                : "transparent",
            border: `1px solid ${
              downloadDone
                ? "var(--tls-border-brand)"
                : (showCta || showCompletionPill)
                  ? "var(--tls-fg-strong)"
                  : "var(--tls-border-subtle)"
            }`,
            color: downloadDone
              ? "var(--tls-brand-ink)"
              : (showCta || showCompletionPill)
                ? "var(--tls-canvas)"
                : isDownloading
                  ? "var(--tls-fg-muted)"
                  : "var(--tls-fg-subtle)",
            cursor: isDownloading ? "wait" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "color var(--motion-duration-fast) var(--motion-ease-standard), background-color var(--motion-duration-fast) var(--motion-ease-standard), border-color var(--motion-duration-fast) var(--motion-ease-standard), border-radius var(--motion-duration-fast) var(--motion-ease-standard)",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {downloadDone ? (
            <CheckCircle style={{ width: 16, height: 16 }} aria-hidden="true" />
          ) : (
            <>
              <Download style={{ width: 15, height: 15 }} aria-hidden="true" />
              {showCompletionPill && (
                <span style={{ fontFamily: "var(--tls-font-body)", fontSize: 13, fontWeight: 600 }}>
                  Download
                </span>
              )}
            </>
          )}
        </button>

        {/* Save heart — 44×44 touch target per WCAG 2.5.5. */}
        <button
          type="button"
          onClick={onFavoriteToggle}
          data-cuelume-press="press"
          // Saving earns the twinkle; un-saving does not. `isSaved` is still
          // the pre-toggle value at pointerup (click fires after), so this
          // reads as "about to save" — one of the few places the catalog's
          // irreverence is allowed to show up in the audio layer.
          data-cuelume-release={isSaved ? "release" : "sparkle"}
          aria-label={isSaved ? `Remove ${title} from saved sounds` : `Save ${title}`}
          title={isSaved ? "Remove from saved sounds" : "Save sound"}
          data-icon-btn
          // Always kept: `.sc-marquee:not(.is-active) .sc-save:not([data-keep])`
          // hides a resting mobile heart, and the copy on /sounds/saved
          // promises one on every sound. `keepSaveOnMobile` and `isSaved`
          // no longer gate it.
          data-keep=""
          className="sc-save tlsr-focus"
          style={{
            width: 44,
            height: 44,
            minWidth: 44,
            minHeight: 44,
            aspectRatio: "1 / 1",
            padding: 0,
            // §17.1: pressed + square → radius-round, same rule as the download
            // button above.
            borderRadius: "var(--ls-radius-round)",
            background: isSaved ? "var(--tls-brand-soft)" : "transparent",
            border: `1px solid ${isSaved ? "var(--tls-border-brand)" : "var(--tls-border-subtle)"}`,
            color: isSaved ? "var(--tls-brand-ink)" : "var(--tls-fg-subtle)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "color var(--motion-duration-fast) var(--motion-ease-standard), background-color var(--motion-duration-fast) var(--motion-ease-standard), border-color var(--motion-duration-fast) var(--motion-ease-standard)",
            flexShrink: 0,
          }}
        >
          <Heart
            style={{ width: 14, height: 14, fill: isSaved ? "currentColor" : "none" }}
            aria-hidden="true"
          />
        </button>
      </div>
    </>
  );
}
